/**
 * Minimal AppSync GraphQL POST helper.
 *
 * Why this exists: the Amplify Data Schema client (@aws-amplify/data-schema
 * + @aws-amplify/api-graphql + graphql language internals) bundles 10-20 KB
 * gzip of machinery — GraphQL parser, lexer, visitor, printer, observeQuery,
 * AWSWebSocketProvider for subscriptions — solely to invoke a JSON POST
 * with an api-key header. For the eager home-route queries (listActivePacks,
 * getPack, listRecentNotablePulls), we issue the raw request directly to
 * evict that machinery from the entry chunk.
 *
 * Other callers (admin, marketplace, account, gifts, etc.) continue to use
 * the Amplify client — those imports already land in lazy route chunks, so
 * they don't bloat the LCP path.
 *
 * Auth mode: API key. AppSync with `x-api-key` is an anonymous request;
 * the api_key value comes from amplify_outputs.json, which Vite inlines at
 * build time (see lib/amplify-client.ts for the same pattern).
 *
 * Errors: throws plain Error with a `[graphqlFetch]` prefix (matching the
 * `[deferIdle]` precedent in app/src/lib/defer-idle.ts). No custom error
 * class — the codebase has no other custom-error-class precedent.
 */
import outputs from '../../amplify_outputs.json';

const ENDPOINT = (outputs as { data?: { url?: string } }).data?.url ?? '';
const API_KEY = (outputs as { data?: { api_key?: string } }).data?.api_key ?? '';

/**
 * Send a GraphQL operation to the AppSync endpoint with API-key auth.
 * Returns the typed `data` field on success. Throws `Error` with a
 * `[graphqlFetch]` prefix on:
 *   - missing endpoint/api_key in amplify_outputs.json
 *   - non-2xx HTTP response
 *   - non-JSON response body (e.g., WAF/CDN HTML error page wrapped 200)
 *   - GraphQL `errors` array in the response body
 *   - missing `data` field in the response
 *   - network failure
 *
 * Callers should NOT pass userPool-auth queries through this helper —
 * use the Amplify client for those (it handles JWT acquisition).
 */
export async function graphqlFetch<TData>(
  query: string,
  variables?: Record<string, unknown>,
): Promise<TData> {
  if (!ENDPOINT || !API_KEY) {
    throw new Error(
      '[graphqlFetch] amplify_outputs.json is missing data.url or data.api_key — Amplify not configured',
    );
  }

  let res: Response;
  try {
    res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
      // JSON.stringify drops `undefined` values from object literals, so
      // when the caller passes no `variables`, the wire body becomes
      // `{"query":"..."}` rather than `{"query":"...","variables":null}`.
      // This is the AppSync-correct shape for variable-less operations.
      body: JSON.stringify({ query, variables }),
    });
  } catch (err) {
    throw new Error(
      `[graphqlFetch] network failure: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  if (!res.ok) {
    throw new Error(`[graphqlFetch] AppSync responded ${res.status} ${res.statusText}`);
  }

  // Wrap res.json() in try/catch — a WAF or CDN may respond 200 OK with
  // an HTML error page (e.g., Cloudflare 524 wrapped as 200). The default
  // res.json() throws raw SyntaxError; rethrow as a [graphqlFetch] error.
  let json: { data?: TData; errors?: Array<{ message: string }> };
  try {
    json = (await res.json()) as typeof json;
  } catch (err) {
    throw new Error(
      `[graphqlFetch] invalid JSON in response body: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  if (json.errors?.length) {
    throw new Error(
      `[graphqlFetch] GraphQL errors: ${json.errors.map((e) => e.message).join('; ')}`,
    );
  }

  if (json.data === undefined || json.data === null) {
    throw new Error('[graphqlFetch] response missing data field');
  }

  return json.data;
}
